import {useState} from 'react';

import {Button, DatePicker, Modal} from '@/components';
import type {BusinessProductUpdateCategory} from '@/types';

import {CATEGORY_STYLES} from '../categoryStyles';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  category: BusinessProductUpdateCategory;
  authorName: string;
  authorRoleLabel: string;
  isSubmitting: boolean;
  onConfirm: (publishAt?: Date) => void;
};

const PublishModal = ({
  isOpen,
  onClose,
  title,
  category,
  authorName,
  authorRoleLabel,
  isSubmitting,
  onConfirm,
}: Props) => {
  const [mode, setMode] = useState<'now' | 'schedule'>('now');
  const [scheduledAt, setScheduledAt] = useState<Date>(
    () => new Date(Date.now() + 60 * 60 * 1000),
  );
  const categoryStyle = CATEGORY_STYLES[category];

  return (
    <Modal
      title="Publish update"
      isOpen={isOpen}
      onClose={onClose}
      width="w-[420px]"
    >
      <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
        This will appear in every Business Portal user&apos;s notification
        panel.
      </p>

      <div className="mb-4 rounded-md border border-gray-700 p-3">
        {categoryStyle && (
          <span
            className={`mb-1 inline-block rounded-full px-2 py-0.5 text-xs font-medium ${categoryStyle.className}`}
          >
            {categoryStyle.label}
          </span>
        )}
        <p className="font-semibold">{title || 'Untitled update'}</p>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Audience: All portal users · Author: {authorName}
          {authorRoleLabel ? ` (${authorRoleLabel})` : ''}
        </p>
      </div>

      <div className="mb-4 flex flex-col gap-2">
        <button
          type="button"
          onClick={() => setMode('now')}
          className={`rounded-md border p-3 text-left ${
            mode === 'now' ? 'border-blue-500' : 'border-gray-700'
          }`}
        >
          <span className="font-medium">Publish now</span>
        </button>
        <button
          type="button"
          onClick={() => setMode('schedule')}
          className={`rounded-md border p-3 text-left ${
            mode === 'schedule' ? 'border-blue-500' : 'border-gray-700'
          }`}
        >
          <span className="font-medium">Schedule</span>
          {mode === 'schedule' && (
            <div className="mt-2">
              <DatePicker
                selectedDate={scheduledAt}
                setSelectedDate={setScheduledAt}
                minDate={new Date()}
                showTime
              />
            </div>
          )}
        </button>
      </div>

      <p className="mb-4 text-xs text-green-500">
        {mode === 'now'
          ? 'Pushed in real time; users see it without refreshing.'
          : `Will publish automatically at the scheduled time.`}
      </p>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="solid"
          colorScheme="cyan"
          isLoading={isSubmitting}
          disabled={mode === 'schedule' && scheduledAt <= new Date()}
          onClick={() =>
            onConfirm(mode === 'schedule' ? scheduledAt : undefined)
          }
        >
          {mode === 'now' ? 'Publish now' : 'Schedule'}
        </Button>
      </div>
    </Modal>
  );
};

export default PublishModal;
